import { useEffect, useState, useMemo } from "react";
import { queryModel, deployModel, fineTuneModel, terminateModel, deleteMIModel } from "../../utils/fine-tuning-functions";
import ConfigureModelModal from "./ConfigureModelModal";
import ConfirmationModal from "./ConfirmationModal"

export default function DisplayModels({ models }) {
  const [openConfigModal, setOpenConfigModal] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedDataset, setSelectedDataset] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [modelToDelete, setModelToDelete] = useState(null);

  // Handle opening the delete confirmation modal
  const handleOpenDeleteModal = (modelId) => {
    setModelToDelete(modelId);
    setDeleteModalOpen(true);
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (!modelToDelete) return;

    try {
      setIsLoading(true);
      await deleteMIModel(modelToDelete);

      // Close the modal
      setDeleteModalOpen(false);
      setModelToDelete(null);
    } catch (error) {
      console.error("Error deleting model:", error);
      // You might want to show an error message to the user
    } finally {
      setIsLoading(false);
    }
  };

  // Handle closing the delete modal
  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setModelToDelete(null);
  };

  const handleOpenConfigModal = async (modelId) => {
    try {
      setIsLoading(true);
      const data = await queryModel(modelId);
      setSelectedModel(data.model);
      setOpenConfigModal(true);
    } catch (error) {
      console.error("Error fetching model details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseConfigModal = () => {
    setOpenConfigModal(false);
    setSelectedModel(null);
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleDatasetChange = (e) => {
    setSelectedDataset(e.target.value);
  };

  // Get unique datasets from models
  const uniqueDatasets = useMemo(() => {
    if (!models) return [];
    return [...new Set(models.map(model => model.dataset_location).filter(Boolean))];
  }, [models]);

  // Get unique statuses from models
  const uniqueStatuses = useMemo(() => {
    if (!models) return [];
    return [...new Set(models.map(model => model.training_status))];
  }, [models]);

  // Filter models based on selected filters
  const filteredModels = useMemo(() => {
    if (!models) return [];

    return models.filter(model => {
      // Filter by status
      if (selectedStatus !== "all" && model.training_status !== selectedStatus) {
        return false;
      }

      // Filter by dataset
      if (selectedDataset && model.dataset_location !== selectedDataset) {
        return false;
      }

      // Filter by search term
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          model.name.toLowerCase().includes(searchLower) ||
          (model.description && model.description.toLowerCase().includes(searchLower)) ||
          (model.model_type && model.model_type.toLowerCase().includes(searchLower))
        );
      }

      return true;
    });
  }, [models, selectedStatus, selectedDataset, searchTerm]);

  // Handle model actions
  const handleFineTune = async (id) => {
    try {
      setIsLoading(true);
      await fineTuneModel(id);
      // You might want to update the models list after successful fine-tuning
    } catch (error) {
      console.error("Error fine-tuning model:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeploy = async (id) => {
    try {
      setIsLoading(true);
      await deployModel(id);
      // You might want to update the models list after successful deployment
    } catch (error) {
      console.error("Error deploying model:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTerminate = async (id) => {
    if (window.confirm("Are you sure you want to terminate this model? This action cannot be undone.")) {
      try {
        setIsLoading(true);
        await terminateModel(id);
        // You might want to update the models list after successful termination
      } catch (error) {
        console.error("Error terminating model:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Helper function to get status badge color
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "COMPLETED":
        return "bg-green-100 text-green-800 border-green-200";
      case "CONFIGURED":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "TRAINING":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "FAILED":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Show loading state if no models
  if (!models) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <header className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-ktx-purple">
            Fine-Tuned Models
          </h1>
          <p className="mt-2 text-gray-600">
            Create and manage custom fine-tuned models for your specific use cases
          </p>
        </header>
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 border-4 border-ktx-purple border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600">Loading models...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <header className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-ktx-purple">
          Fine-Tuned Models
        </h1>
        <p className="mt-2 text-gray-600">
          Create and manage custom fine-tuned models for your specific use cases
        </p>
      </header>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm mb-8 p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="search"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-ktx-purple focus:border-ktx-purple sm:text-sm"
                placeholder="Search models by name or description..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>

          <div className="flex-shrink-0 w-full md:w-auto">
            <select
              className="block w-full rounded-md border-gray-300 focus:border-ktx-purple focus:ring-ktx-purple sm:text-sm"
              value={selectedDataset}
              onChange={handleDatasetChange}
            >
              <option value="">All Datasets</option>
              {uniqueDatasets.map((dataset, idx) => (
                <option key={idx} value={dataset}>
                  {dataset}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Status filter buttons */}
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => handleStatusChange("all")}
            className={`px-3 py-1 rounded-full text-sm font-medium ${selectedStatus === "all"
              ? "bg-ktx-purple text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
          >
            All
          </button>
          {uniqueStatuses.map((status, idx) => (
            <button
              key={idx}
              onClick={() => handleStatusChange(status)}
              className={`px-3 py-1 rounded-full text-sm font-medium ${selectedStatus === status
                ? "bg-ktx-purple text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Models Grid */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
        {filteredModels.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredModels.map((model) => (
              <div key={model.id} className="border border-gray-200 rounded-lg overflow-hidden flex flex-col transition-shadow hover:shadow-md relative">
                <div className="p-4 flex-1">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-lg text-gray-900 truncate" title={model.name}>
                      {model.name}
                    </h3>

                    {/* Training Status Badge */}
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadgeClass(model.training_status)}`}
                    >
                      {model.training_status}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                    {model.description || "No description provided"}
                  </p>

                  <div className="mt-2 space-y-2 text-sm">
                    {/* Model ID */}
                    <div className="flex items-start">
                      <span className="text-gray-500 min-w-[120px]">Model ID:</span>
                      <span className="text-gray-700">#{model.id}</span>
                    </div>

                    {/* Base Model */}
                    <div className="flex items-start">
                      <span className="text-gray-500 min-w-[120px]">Base Model:</span>
                      <span className="text-gray-700 truncate" title={model.base_model}>
                        {model.base_model ? model.base_model.split('/').pop() : "Not specified"}
                      </span>
                    </div>

                    {/* Model Type */}
                    <div className="flex items-start">
                      <span className="text-gray-500 min-w-[120px]">Model Type:</span>
                      <span className="text-gray-700">{model.model_type || "Not specified"}</span>
                    </div>

                    {/* Dataset */}
                    <div className="flex items-start">
                      <span className="text-gray-500 min-w-[120px]">Dataset:</span>
                      <span className="text-gray-700 truncate" title={model.dataset_location}>
                        {model.dataset_location ? model.dataset_location.split('/').pop() : "Not configured"}
                      </span>
                    </div>

                    {/* HF Model Name */}
                    {model.huggingface_model_name && (
                      <div className="flex items-start">
                        <span className="text-gray-500 min-w-[120px]">HF Model:</span>
                        <span className="text-gray-700 truncate" title={model.huggingface_model_name}>
                          {model.huggingface_model_name.split('/').pop()}
                        </span>
                      </div>
                    )}

                    {/* Model Version */}
                    {model.model_version && (
                      <div className="flex items-start">
                        <span className="text-gray-500 min-w-[120px]">Version:</span>
                        <span className="text-gray-700">{model.model_version}</span>
                      </div>
                    )}

                    {/* Training Settings Summary */}
                    {model.hyperparameters && (
                      <div className="flex items-start">
                        <span className="text-gray-500 min-w-[120px]">Training:</span>
                        <span className="text-gray-700">
                          {model.hyperparameters.max_steps} steps, LR {model.hyperparameters.learning_rate}
                        </span>
                      </div>
                    )}

                    {/* Deployment URL & Health Status */}
                    {model.deployment_url && (
                      <>
                        <div className="flex items-start">
                          <span className="text-gray-500 min-w-[120px]">Deployment:</span>
                          <div className="flex flex-col">
                            <a
                              href={model.deployment_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-ktx-purple hover:underline truncate"
                              title={model.deployment_url}
                            >
                              {model.deployment_url.replace(/^https?:\/\//, '')}
                            </a>

                            {/* Clean health status indicator */}
                            <span className={`mt-1 text-xs ${model.deployment_health_check === "HEALTHY"
                              ? "text-green-600"
                              : model.deployment_health_check === "UNHEALTHY"
                                ? "text-red-600"
                                : "text-gray-500"
                              }`}>
                              Status: {model.deployment_health_check || "Unknown"}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Card footer with action buttons */}
                <div className="bg-gray-50 p-3 border-t border-gray-200">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleOpenConfigModal(model.id)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ktx-purple"
                      disabled={isLoading}
                    >
                      <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Configure
                    </button>

                    {model.training_status === "CONFIGURED" && (
                      <button
                        onClick={() => handleFineTune(model.id)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-ktx-purple hover:bg-ktx-purple-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ktx-purple"
                        disabled={isLoading}
                      >
                        <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Fine-Tune
                      </button>
                    )}

                    {model.training_status === "COMPLETED" && !model.deployment_url && (
                      <button
                        onClick={() => handleDeploy(model.id)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        disabled={isLoading}
                      >
                        <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Deploy
                      </button>
                    )}

                    {model.instance_id && model.deployment_url && model.deployment_health_check === "HEALTHY" &&(
                      <button
                        onClick={() => handleTerminate(model.id)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        disabled={isLoading}
                      >
                        <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Terminate
                      </button>
                    )}

                    {model.training_status === "CONFIGURED" && (
                      <button
                        onClick={() => handleOpenDeleteModal(model.id)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-red-600 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        disabled={isLoading}
                      >
                        <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12">
            <svg className="h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900">No models found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedDataset || selectedStatus !== "all"
                ? "Try adjusting your filters to see more results"
                : "You don't have any models yet. Start by configuring a new model."}
            </p>
          </div>
        )}
      </div>

      {/* Info cards at the bottom */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-md border border-gray-200">
          <h4 className="font-medium text-ktx-purple flex items-center">
            <svg className="h-5 w-5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Configuration
          </h4>
          <ul className="mt-2 space-y-1 text-sm text-gray-600">
            <li>• Set up base model parameters</li>
            <li>• Choose training hyperparameters</li>
            <li>• Select your custom dataset</li>
          </ul>
        </div>

        <div className="bg-white p-4 rounded-md border border-gray-200">
          <h4 className="font-medium text-ktx-purple flex items-center">
            <svg className="h-5 w-5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            Fine-Tuning
          </h4>
          <ul className="mt-2 space-y-1 text-sm text-gray-600">
            <li>• Train models on custom data</li>
            <li>• Monitor training progress</li>
            <li>• Evaluate model performance</li>
          </ul>
        </div>

        <div className="bg-white p-4 rounded-md border border-gray-200">
          <h4 className="font-medium text-ktx-purple flex items-center">
            <svg className="h-5 w-5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 11l7-7 7 7M5 19l7-7 7 7" />
            </svg>
            Deployment
          </h4>
          <ul className="mt-2 space-y-1 text-sm text-gray-600">
            <li>• Deploy models to production</li>
            <li>• Get API endpoints for inference</li>
            <li>• Manage deployment resources</li>
          </ul>
        </div>
      </div>

      {/* Configure Model Modal */}
      {openConfigModal && selectedModel && (
        <ConfigureModelModal model={selectedModel} closeModal={handleCloseConfigModal} />
      )}

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete Model"
        message="Are you sure you want to delete this model? This action cannot be undone and all associated data will be permanently removed."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Configure Model Modal */}
      {openConfigModal && selectedModel && (
        <ConfigureModelModal model={selectedModel} closeModal={handleCloseConfigModal} />
      )}
    </div>
  );
}
